import { apiClient } from '@/lib/api-client';
import { API_CONFIG } from '@/config/api';

/**
 * Auth API response interface
 */
interface AuthResponse {
  success: boolean;
  status?: boolean;
  message: string;
  data?: UserData;
  token?: string;
  errors?: Record<string, string[]>;
}

/**
 * Register request interface
 */
export interface RegisterRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
  company_gst?: string;
  company_name?: string;
}

/**
 * User data interface
 */
export interface UserData {
  id: number;
  customer_code: string;
  email: string;
  name: string;
  first_name: string;
  last_name: string;
  gender: string;
  date_of_birth: string;
  phone: string;
  status: number;
  group: {
    id: number;
    name: string;
    created_at: string;
    updated_at: string;
  };
  notes: string | null;
  created_at: string;
  updated_at: string;
  alternative_number: string;
  business_number: string;
  business_email: string;
  whatsapp_number: string;
  company_url: string;
  customer_type: string;
  payment_access: any;
  payment_terms: any;
  company_name: string;
  company_gst: string;
  heard_about_us: any;
  legal_name: any;
  gst_treatment: any;
  is_register_customer: string;
}

/**
 * Auth service for handling authentication-related API calls
 */
export class AuthService {
  /**
   * Request OTP for phone number
   * @param phone - Phone number to send OTP to
   * @returns Promise with response
   */
  static async requestOTP(phone: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(`${API_CONFIG.ENDPOINTS.AUTH}/request-otp`, {
      phone,
    });

    return response;
  }

  /**
   * Verify OTP and login
   * @param phone - Phone number
   * @param otp - OTP code
   * @param cartId - Optional cart ID to merge guest cart with user cart
   * @returns Promise with login response including token and user data
   */
  static async verifyOTP(phone: string, otp: string, cartId?: string): Promise<AuthResponse> {
    const requestBody: any = {
      phone,
      mobile_pin: otp,
    };

    // Add cart_id if provided
    if (cartId) {
      requestBody.cart_id = cartId;
      console.log('Sending cart_id with login:', cartId);
    }

    const response = await apiClient.post<AuthResponse>(`${API_CONFIG.ENDPOINTS.AUTH}/login-otp`, requestBody);

    return response;
  }

  /**
   * Register new customer account
   * @param data - Registration data
   * @returns Promise with registration response including token and user data
   */
  static async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(`${API_CONFIG.ENDPOINTS.AUTH}/register`, data);

    return response;
  }
}